import logoPrimary from '@assets/images/common/brand/logo-primary.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { tenantAuthStatusApiClient } from '@src/api/tenant/auth/tenantAuthStatusApiClient.ts';
import { LoginRequest, tenantLoginApiClient } from '@src/api/tenant/auth/tenantLoginApiClient';
import { fetchTenantUserProfile } from '@src/context/auth/ProfileFetcher';
import { useTenantAuth } from '@src/context/auth/useTenantAuth';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button';
import { handlePostLoginRedirect } from '@src/utils/redirectAfterLogin';
import { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { TogglePassword } from './components/TogglePassword';

function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<LoginRequest>();

  const [generalError, setGeneralError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const [isRevealPassword, setIsRevealPassword] = useState(false);
  const [isSubmitButtonPressed, setIsSubmitButtonPressed] = useState(false);
  const tenantAuth = useTenantAuth();
  const toast = useToast();

  useEffect(() => {
    if (errors.email || generalError) {
      if (emailRef.current) {
        emailRef.current.style.backgroundColor = 'rgba(251,233,238,0.7)';
        emailRef.current.style.borderColor = '#E0245E';
      }
    }
    if (errors.password || generalError) {
      if (passwordRef.current) {
        passwordRef.current.style.backgroundColor = 'rgba(251,233,238,0.7)';
        passwordRef.current.style.borderColor = '#E0245E';
      }
    }
  }, [errors, generalError]);

  const onSubmit: SubmitHandler<LoginRequest> = async (data) => {
    clearErrors();
    setGeneralError(null);
    setIsSubmitButtonPressed(true);
    const res = await tenantLoginApiClient.login(data);
    handleApiResponse(
      res,
      async (responseParams) => {
        if (responseParams.two_factor_enabled) {
          toast(TOAST_MESSAGE.SEND);
          navigate(TENANT_URL.TWO_FACTOR, {
            state: {
              email: data.email,
              password: data.password,
              isFirstLogin: responseParams.is_first_login,
              session: responseParams.session,
              redirect: searchParams.get('redirect') ?? undefined,
            },
          });
          return;
        }

        localStorage.setItem('tenantMenus', '');
        if (responseParams.is_first_login) {
          navigate(TENANT_URL.FIRST_PASSWORD_RESET, {
            state: { email: data.email },
          });
        } else {
          fetchTenantUserProfile(tenantAuth.setProfile);

          if (handlePostLoginRedirect(navigate, searchParams)) {
            return;
          }

          const res = await tenantAuthStatusApiClient.status();
          handleApiResponse(res, (responseParams) => {
            if (responseParams.first_view_link) {
              navigate(responseParams.first_view_link);
            }
          });
        }
      },
      (errors) => {
        setGeneralError(
          errors.messages?.[0] ||
            'ログインに失敗しました。入力内容に誤りがないか確認してください。',
        );
        setIsSubmitButtonPressed(false);
      },
    );
  };

  const togglePassword = () => {
    setIsRevealPassword((prevState) => !prevState);
  };

  return (
    <div className='flex flex-col gap-8'>
      <div className='grid place-items-center'>
        <img
          alt={import.meta.env.VITE_APP_NAME}
          className='h-auto max-h-[72px] w-[400px] object-contain'
          src={logoPrimary}
        />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-[6px]'>
            <label
              className='label-login'
              htmlFor='email'
            >
              メールアドレス
            </label>
            <input
              id='email'
              className={`input-login ${errors.email || generalError ? 'error-input' : ''}`}
              placeholder='メールアドレス'
              {...register('email')}
            />
          </div>
          <div className='flex flex-col gap-[6px]'>
            <label
              className='label-login'
              htmlFor='password'
            >
              パスワード
            </label>
            <div className='relative'>
              <input
                id='password'
                type={isRevealPassword ? 'text' : 'password'}
                className={`input-login ${errors.password || generalError ? 'error-input' : ''}`}
                placeholder='パスワード'
                {...register('password')}
              />
              <button
                type='button'
                onClick={togglePassword}
                className='toggle-password'
              >
                <TogglePassword isReveal={isRevealPassword} />
              </button>
            </div>
          </div>
        </div>
        {generalError && <div className='error-message'>{generalError}</div>}
        <div className='mt-4 flex justify-center'>
          <Link
            className='text-sm text-primary-800 underline hover:no-underline'
            to={TENANT_URL.PASSWORD_RESET_SENDMAIL}
          >
            パスワードを忘れた場合
          </Link>
        </div>
        <Button
          variant='tenant_primary'
          fullWidth
          fullRounded
          className='mt-6'
          disabled={isSubmitButtonPressed}
          type='submit'
        >
          管理者ログイン
        </Button>
      </form>
    </div>
  );
}

export default LoginForm;
